/* Setting somebody up in the other systems: SignIn, the Training Matrix and EZ Leave.
 *
 * WHY THIS EXISTS
 *
 * In the normal route HR and Accounts finish a case in the Onboarding app, and pressing Complete asks
 * the onboarding Function (/api/joiner) to create the person in those three systems. IT skipping HR
 * and Accounts (the override in Add a joiner, or an already-stamped person who was never created
 * anywhere) needs exactly the same records, so Blueprint asks the SAME endpoint, with the details HR and
 * Accounts would have supplied typed in by hand: the working pattern and holiday entitlement above all.
 *
 * Nothing here writes to those systems. The Function does, as its own identity, one site grant each, and
 * every step of it is idempotent (it looks first), so pressing this twice, or for somebody who is already
 * in one of them, never makes a second record. This file only decides what to ask and how to say the
 * answer, which is why it is pure and tested.
 *
 * WHAT IT DOES NOT COLLECT: national insurance, tax code and bank details. They are payroll data for
 * Accounts' own restricted list and the Function does not take them; entering them is still Accounts' job.
 */

export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'] as const;

/** The details the Function takes for the EZ Leave record, as typed on a form (so the entitlement is text). */
export interface IPayrollInput {
  /** The days they work. A blank pattern is not neutral in EZ Leave: it means no working days. */
  workingPattern: string[];
  /** Their holiday entitlement for a full year, in days. */
  entitlement: string;
  employeeNumber: string;
  companySickPay: boolean;
  rolledUpHolidayPay: boolean;
}

/** Monday to Friday, which is what almost everybody works and what Accounts' form starts at. */
export const defaultPayroll = (): IPayrollInput => ({
  workingPattern: [...WEEKDAYS],
  entitlement: '',
  employeeNumber: '',
  companySickPay: false,
  rolledUpHolidayPay: false,
});

/** A number of days between 0 and 100, like Accounts' form accepts. */
export function validEntitlement(v: string): boolean {
  const t = v.trim();
  if (!/^\d+(\.\d+)?$/.test(t)) return false;
  const n = Number(t);
  return n >= 0 && n <= 100;
}

/** What is still needed before EZ Leave can be created COMPLETE. Without both a pattern and an entitlement
 *  the Function would leave the record awaiting details, which is a record that exists and works for
 *  nothing, so the form insists rather than leaving it for later. */
export function payrollProblems(p: IPayrollInput): string[] {
  const out: string[] = [];
  if (!WEEKDAYS.some((d) => p.workingPattern.includes(d))) out.push('Choose the days they work.');
  if (!validEntitlement(p.entitlement)) out.push('Enter their holiday entitlement in days, like 28.');
  return out;
}

export type SystemsStepId = 'signin' | 'training' | 'leave';

/** The steps this asks for, in the Function's own order. The folder and the Blueprint stamp are left out
 *  on purpose: Blueprint makes the folder (with the welcome) and stamps the person itself. */
export const SYSTEMS_STEPS: SystemsStepId[] = ['signin', 'training', 'leave'];

export const SYSTEMS_LABEL: Record<SystemsStepId, string> = {
  signin: 'SignIn',
  training: 'Training Matrix',
  leave: 'EZ Leave',
};

/** Who to set up: the person's row and, where the wizard has them, the draft's values over it. */
export interface ISystemsJoiner {
  upn: string;
  name: string;
  mail?: string;
  entraId?: string;
  /** Where the welcome went: an address outside the company is kept as their personal email in EZ Leave. */
  welcomeTo?: string;
  /** Hull, Doncaster or London: the name, never the id. */
  depot: string;
  department?: string;
  jobTitle?: string;
  /** ISO date. */
  start?: string;
}

export interface ISystemsRequest {
  joiner: ISystemsJoiner;
  payroll: IPayrollInput;
  /** Runs every check and writes nothing. */
  dryRun?: boolean;
}

const clean = (s: string | undefined): string | undefined => {
  const t = (s ?? '').trim();
  return t ? t : undefined;
};

/** The body the Function expects. Pay options are sent only when set: the Function writes nothing that
 *  was not given, and an unticked box is "nobody said", not "no". */
export function systemsBody(req: ISystemsRequest): Record<string, unknown> {
  const j = req.joiner;
  const p = req.payroll;
  return {
    phase: 'complete',
    steps: SYSTEMS_STEPS,
    ...(req.dryRun ? { dryRun: true } : {}),
    joiner: {
      upn: j.upn.trim(),
      name: j.name.trim(),
      mail: clean(j.mail),
      entraId: clean(j.entraId),
      welcomeTo: clean(j.welcomeTo),
      depot: j.depot.trim(),
      department: clean(j.department),
      jobTitle: clean(j.jobTitle),
      start: clean(j.start)?.slice(0, 10),
    },
    payroll: {
      workingPattern: WEEKDAYS.filter((d) => p.workingPattern.includes(d)),
      entitlement: Number(p.entitlement),
      ...(clean(p.employeeNumber) ? { employeeNumber: p.employeeNumber.trim() } : {}),
      ...(p.companySickPay ? { companySickPay: true } : {}),
      ...(p.rolledUpHolidayPay ? { rolledUpHolidayPay: true } : {}),
    },
  };
}

export interface ISystemsStepResult {
  status: 'done' | 'exists' | 'skipped' | 'failed';
  message: string;
  problems?: string[];
}

export interface ISystemsOutcome {
  /** Every step is settled: it was created or it was already there. */
  ok: boolean;
  /** One paragraph in words, for the person who pressed the button. */
  message: string;
  steps: Partial<Record<SystemsStepId, ISystemsStepResult>>;
}

/** The joiner to send, from a person's row. `depot` is the depot's NAME (Hull), which the register may hold
 *  as an id, so the caller resolves it; the Function drops a depot it does not recognise rather than failing
 *  and says so. */
export function joinerFromPerson(
  p: { name: string; upn: string; email?: string; graphId?: string; dept?: string; title?: string; start?: string },
  depot: string,
  welcomeTo?: string,
): ISystemsJoiner {
  return {
    upn: p.upn,
    name: p.name,
    mail: p.email,
    entraId: p.graphId,
    welcomeTo,
    depot,
    department: p.dept,
    jobTitle: p.title,
    start: p.start,
  };
}

const settled = (r: ISystemsStepResult | undefined): boolean => !!r && (r.status === 'done' || r.status === 'exists');

/** The Function's answer, in words.
 *
 *  A 207 is "some steps failed" and is a result to show, not an error; 401 and 403 are said the way the
 *  folder call says them, because they have the same two causes (the sign-in, or the Onboarder role). */
export function describeSystems(
  status: number,
  body: { steps?: Partial<Record<SystemsStepId, ISystemsStepResult>>; error?: string } | null,
): ISystemsOutcome {
  const fail = (message: string): ISystemsOutcome => ({ ok: false, message, steps: {} });
  if (status === 401) return fail('The onboarding service did not accept the sign-in. Try signing out and back in.');
  if (status === 403) {
    return fail(
      'This account cannot set people up in SignIn, the Training Matrix and EZ Leave. It needs the Onboarder role, ' +
      'which comes from SG_Hird Hub L2 Admin. Check which account is showing at the bottom left.',
    );
  }
  if (status === 404) {
    return fail('The onboarding service has no setup endpoint (/joiner). func-hird-onboarding needs republishing with it.');
  }
  if (status !== 200 && status !== 207) return fail(body?.error || `The onboarding service answered ${status}.`);

  const steps = body?.steps ?? {};
  const asked = SYSTEMS_STEPS.filter((s) => steps[s]);
  if (asked.length === 0) return fail('The onboarding service answered but reported nothing, so nothing is known to have been set up.');

  const lines = asked.map((s) => `${SYSTEMS_LABEL[s]}: ${steps[s]!.message}`);
  const notes = asked.flatMap((s) => (steps[s]!.problems ?? []).map((p) => `${SYSTEMS_LABEL[s]}: ${p}`));
  const ok = asked.every((s) => settled(steps[s]));
  return {
    ok,
    message: [...lines, ...notes.map((n) => `Note: ${n}`)].join(' '),
    steps,
  };
}
